import React from 'react';
import { StyleSheet, View } from 'react-native';
import {
  Container,
  MobileHeader,
  Modal,
  theme,
  useResponsive,
} from '@sweetgreen/garnish';

import type { TransactionHistoryQuery } from '@order/graphql';
import { useLocalizationContext } from '@order/Localization';

import { useNavigateBack } from '../../navigation';
import { SweetpassBillingHistory } from './components';
import { useSweetpassBillingHistoryNavigationOptions } from './hooks';

// ─────────────────────────────────────────────────────────────────────────────

export const SweetpassBillingHistoryScreenContent = (
  props: SweetpassBillingHistoryScreenContentProps,
) => {
  const { currentBreakpoint } = useResponsive();
  const { isXS } = currentBreakpoint;

  useSweetpassBillingHistoryNavigationOptions();

  if (isXS) return <SweetpassBillingHistoryScreenContentXS {...props} />;

  return <SweetpassBillingHistoryScreenContentSM {...props} />;
};

// ─── Responsive Components ───────────────────────────────────────────────────

const SweetpassBillingHistoryScreenContentXS = (
  props: SweetpassBillingHistoryScreenContentProps,
) => {
  const { billingHistory, isFetching } = props;

  const { t } = useLocalizationContext();

  return (
    <Container style={styles.containerXS}>
      <MobileHeader
        title={t('account.sweetpass-membership.billing-history.header')}
      />

      <SweetpassBillingHistory
        billingHistory={billingHistory}
        isFetching={isFetching}
      />
    </Container>
  );
};

const SweetpassBillingHistoryScreenContentSM = (
  props: SweetpassBillingHistoryScreenContentProps,
) => {
  const { billingHistory, isFetching } = props;

  const { t } = useLocalizationContext();
  const handleGoBack = useNavigateBack();

  return (
    <>
      <Modal.Header onClose={handleGoBack}>
        {t('account.sweetpass-membership.billing-history.header')}
      </Modal.Header>

      <View style={styles.containerSM}>
        <SweetpassBillingHistory
          billingHistory={billingHistory}
          isFetching={isFetching}
        />
      </View>
    </>
  );
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  containerXS: {
    marginBottom: theme.spacing['6'],
    paddingBottom: theme.spacing['10'],
  },
  containerSM: {
    marginBottom: theme.spacing['6'],
    paddingBottom: theme.spacing['10'],
    paddingHorizontal: theme.spacing['6'],
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type SweetpassBillingHistoryScreenContentProps = Readonly<{
  billingHistory: TransactionHistory;
  isFetching: boolean;
}>;

type TransactionHistory = NonNullable<
  TransactionHistoryQuery['paymentSubscriptionProfile']
>['transactionHistory'];
